import { Signal } from "@engine";
import type { StatBumpEvent, StatsBlob, StatsService } from "./StatsService";

const STORAGE_KEY = "morph.achievements.v1";

// Stable IDs that other systems (cosmetics in particular) gate on.
// Treat these like an enum: never rename, never reuse. Adding a new
// achievement is fine; removing one breaks anyone whose unlock set
// references it (we just ignore unknown IDs at load time).
export type AchievementId =
  | "first_run"
  | "distance_500"
  | "distance_2k"
  | "combo_25"
  | "combo_50"
  | "perfect_10_run"
  | "perfect_100_total"
  | "save_first"
  | "boss_first"
  | "boss_5_total"
  | "boss_all_four"
  | "frenzy_first"
  | "frenzy_3_in_run"
  | "daily_7"
  | "all_shapes_one_run"
  | "apex_clear"
  | "hard_runner"
  | "magnetised";

export interface AchievementDef {
  id: AchievementId;
  title: string;
  description: string;
  // Tier label mostly for UI grouping ("bronze", "silver", "gold").
  // Values are illustrative; the StatsScreen renders them as colour
  // hints rather than enforcing an order.
  tier: "bronze" | "silver" | "gold";
  // Predicate against the most recent stats snapshot. Pure: returns
  // true when the achievement should be unlocked. The service guards
  // against double-emitting onUnlocked.
  predicate: (s: StatsBlob, e?: StatBumpEvent) => boolean;
}

// 15 achievements. The catalogue order doubles as the display order in
// the StatsScreen, so we cluster by theme: progression, scoring,
// combat, frenzy/boss, daily/long-tail.
export const ACHIEVEMENTS: AchievementDef[] = [
  {
    id: "first_run",
    title: "First Steps",
    description: "Finish your first run.",
    tier: "bronze",
    predicate: (s) => s.runs >= 1,
  },
  {
    id: "distance_500",
    title: "Half Klick",
    description: "Travel 500 m total.",
    tier: "bronze",
    predicate: (s) => s.distanceTotal >= 500,
  },
  {
    id: "distance_2k",
    title: "Marathon",
    description: "Travel 2,000 m total.",
    tier: "silver",
    predicate: (s) => s.distanceTotal >= 2000,
  },
  {
    id: "combo_25",
    title: "On A Roll",
    description: "Reach a 25-segment combo.",
    tier: "bronze",
    predicate: (s) => s.longestCombo >= 25,
  },
  {
    id: "combo_50",
    title: "Untouchable",
    description: "Reach a 50-segment combo.",
    tier: "gold",
    predicate: (s) => s.longestCombo >= 50,
  },
  {
    id: "perfect_10_run",
    title: "In The Pocket",
    description: "Land 10 perfect rotations in a single run.",
    tier: "silver",
    predicate: (s) => s.perfectsThisRun >= 10,
  },
  {
    id: "perfect_100_total",
    title: "Metronome",
    description: "Land 100 perfect rotations lifetime.",
    tier: "silver",
    predicate: (s) => s.perfectsTotal >= 100,
  },
  {
    id: "save_first",
    title: "Close Call",
    description: "Pull off a last-chance save.",
    tier: "bronze",
    predicate: (s) => s.savesTotal >= 1,
  },
  {
    id: "boss_first",
    title: "Giant Slayer",
    description: "Clear your first boss bar.",
    tier: "bronze",
    predicate: (s) => s.bossesTotal >= 1,
  },
  {
    id: "boss_5_total",
    title: "Boss Hunter",
    description: "Clear 5 boss bars.",
    tier: "silver",
    predicate: (s) => s.bossesTotal >= 5,
  },
  {
    id: "boss_all_four",
    title: "Quadrilogy",
    description: "Clear every named boss at least once.",
    tier: "gold",
    predicate: (s) =>
      s.bossesById.includes("sweep_gauntlet") &&
      s.bossesById.includes("pulse_storm") &&
      s.bossesById.includes("mirror_maze") &&
      s.bossesById.includes("final_boss"),
  },
  {
    id: "frenzy_first",
    title: "Tilt",
    description: "Trigger Frenzy mode.",
    tier: "bronze",
    predicate: (s) => s.frenziesTotal >= 1,
  },
  {
    id: "frenzy_3_in_run",
    title: "Maxed Out",
    description: "Trigger Frenzy 3 times in a single run.",
    tier: "gold",
    predicate: (s) => s.frenziesThisRun >= 3,
  },
  {
    id: "daily_7",
    title: "Daily Driver",
    description: "Play the daily challenge on 7 different days.",
    tier: "silver",
    predicate: (s) => s.dailyKeysPlayed.length >= 7,
  },
  {
    id: "all_shapes_one_run",
    title: "Shape-Shifter",
    description: "Visit every shape in a single run.",
    tier: "silver",
    // 5 base shapes once pentagon + hexagon land. We compare against
    // length so adding more shapes later doesn't require touching the
    // predicate in lock-step with the Shape union.
    predicate: (s) => s.shapesThisRun.length >= 5,
  },
  {
    id: "apex_clear",
    title: "Apex Predator",
    description: "Clear the Apex Gate boss.",
    tier: "gold",
    // MorphHexGame bumps a synthetic "apex_gate" boss id alongside
    // the real reverse_gate marker when the Apex Gate banner clears,
    // so this predicate can safely look up the bossesById set.
    predicate: (s) => s.bossesById.includes("apex_gate"),
  },
  {
    id: "hard_runner",
    title: "Hard Runner",
    description: "Finish a run on Hard with at least 600m of distance.",
    tier: "gold",
    // Predicate also reads the bump event so we only fire on a
    // runEnded that explicitly carried difficulty="hard". The
    // distance threshold compares against the per-event delta
    // (StatsService.recordRunEnded encodes the run distance there).
    predicate: (_s, e) =>
      e?.kind === "runEnded" &&
      e.difficulty === "hard" &&
      (e.delta ?? 0) >= 600,
  },
  {
    id: "magnetised",
    title: "Magnetised",
    description: "Collect 5 magnet pickups in a single run.",
    tier: "silver",
    predicate: (s) => s.magnetsThisRun >= 5,
  },
];

// Public API the rest of the game (CosmeticsService, AchievementToast,
// StatsScreen) reads. The unlock set is a Set<AchievementId> persisted
// in localStorage so progress survives reloads + private-tab quirks.
export class AchievementService {
  readonly onUnlocked = new Signal<AchievementDef>();
  private stats: StatsService;
  private unlocked = new Set<AchievementId>();
  private unsub: (() => void) | null = null;

  constructor(stats: StatsService) {
    this.stats = stats;
    this.load();
    // Predicates run on every stat bump. ~15 predicates is tiny, and
    // bumps happen on player actions (handful per second at most), so
    // this is comfortably below profiling noise.
    this.unsub = this.stats.onStatBumped.on((e) => this.evaluateAll(e));
  }

  isUnlocked(id: AchievementId): boolean {
    return this.unlocked.has(id);
  }

  getAll(): AchievementDef[] {
    return ACHIEVEMENTS;
  }

  getUnlockedSet(): Set<AchievementId> {
    return new Set(this.unlocked);
  }

  // Evaluate a single bump against every achievement. We pass the
  // event in so future predicates can react to "delta of N this
  // event" (e.g. "land 3 perfects in one frame") without us having to
  // diff stats blobs ourselves.
  private evaluateAll(e: StatBumpEvent): void {
    const snapshot = this.stats.getAll();
    let dirty = false;
    for (const def of ACHIEVEMENTS) {
      if (this.unlocked.has(def.id)) continue;
      if (!def.predicate(snapshot, e)) continue;
      this.unlocked.add(def.id);
      dirty = true;
      this.onUnlocked.emit(def);
    }
    if (dirty) this.persist();
  }

  // Manual trigger (debug) -- evaluate all predicates against the
  // current stats snapshot without needing a bump. Useful when wiring
  // a brand-new player whose stats blob already satisfies criteria
  // (e.g. someone playing without StatsService in a previous build).
  evaluateNow(): void {
    this.evaluateAll({ kind: "runEnded" });
  }

  clear(): void {
    this.unlocked.clear();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  dispose(): void {
    this.unsub?.();
    this.unsub = null;
    this.onUnlocked.clear();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return;
      const known = new Set<AchievementId>(ACHIEVEMENTS.map((a) => a.id));
      for (const id of parsed) {
        if (typeof id === "string" && known.has(id as AchievementId)) {
          this.unlocked.add(id as AchievementId);
        }
      }
    } catch {
      // ignore corrupted blob; user just regains progress
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(this.unlocked))
      );
    } catch {
      // private mode / quota errors -- ignore
    }
  }
}
